import type { FactCheckRequest, FactCheckResult } from "@/api/fact-check-api"
import type { ActivityMessage, ChatAppRequest, ServerMessage } from "@/api/models"

import { WebSocketClient } from "@/api/web-socket-client"
import { BACKEND_URI } from "@/constants/config"
import { logger } from "@/lib/logger"
import { SettingsService } from "@/services/settings-service"

import { publishCrossTabEvent } from "./fact-check-cross-tab"

type FactCheckJobStatus = "running" | "completed" | "error" | "cancelled"

type CompletedFactCheckResult = {
  sessionType: string
  sessionId?: string
  messageId: string
  result: FactCheckResult
  completedAt: number
}

/**
 * Lookup key for cached fact-check results stored by the job manager.
 * Allows callers to retrieve or clear a recently completed result without
 * waiting for the server to persist and return it in subsequent fetches.
 */
type CompletedFactCheckResultLookup = {
  sessionType: string
  sessionId?: string
  messageId: string
}

const COMPLETED_RESULT_TTL_MS = 1000 * 60 * 30 // 30 minutes

export type FactCheckJobCompletedEvent = {
  type: "fact_check_completed"
  jobId: string
  sessionId?: string
  sessionType: string
  messageId?: string
  result: FactCheckResult
}

export type FactCheckJobErrorEvent = {
  type: "fact_check_error"
  jobId: string
  sessionId?: string
  sessionType: string
  messageId?: string
  error: Error
}

export type FactCheckJobEvent = FactCheckJobCompletedEvent | FactCheckJobErrorEvent

type FactCheckJob = {
  jobId: string
  status: FactCheckJobStatus
  sessionType: string
  sessionId?: string
  messageId?: string
  request: FactCheckRequest
  startedAt: number
  completedAt?: number
  lastActivity?: ActivityMessage
  abortController: AbortController
  cancelled?: boolean
  result?: FactCheckResult
  error?: Error
}

type StartFactCheckJobOptions = {
  sessionType: string
  sessionId?: string
  messageId?: string
  request: FactCheckRequest
  onActivity?: (message: string) => void
}

/**
 * Singleton job manager that keeps fact-check WebSockets alive across navigation.
 * It emits completion events so the app can toast + deep-link back to the message.
 */
export class FactCheckJobManager {
  private static instance: FactCheckJobManager | null = null

  static getInstance(): FactCheckJobManager {
    if (!FactCheckJobManager.instance) {
      FactCheckJobManager.instance = new FactCheckJobManager()
    }
    return FactCheckJobManager.instance
  }

  private listeners = new Set<(event: FactCheckJobEvent) => void>()
  private jobsById = new Map<string, FactCheckJob>()
  private activeJobIdByMessageId = new Map<string, string>()
  private completedResultsByKey = new Map<string, CompletedFactCheckResult>()
  private cleanupTimers = new Map<string, ReturnType<typeof setTimeout>>()

  subscribe(listener: (event: FactCheckJobEvent) => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  getJob(jobId: string): FactCheckJob | undefined {
    return this.jobsById.get(jobId)
  }

  /**
   * Retrieve a cached completed result when available. Results are retained
   * briefly to bridge the gap between completion and the next message fetch.
   */
  getCompletedResult(options: CompletedFactCheckResultLookup): FactCheckResult | undefined {
    this.cleanupCompletedResults()
    return this.completedResultsByKey.get(this.buildCompletedResultKey(options))?.result
  }

  /**
   * Clear a cached completed result once it has been reconciled with server data.
   */
  clearCompletedResult(options: CompletedFactCheckResultLookup): void {
    this.completedResultsByKey.delete(this.buildCompletedResultKey(options))
  }

  cancel(jobId: string): void {
    const job = this.jobsById.get(jobId)
    if (!job) return
    if (job.status !== "running") return

    job.status = "cancelled"
    job.cancelled = true
    job.abortController.abort()
    if (job.messageId && this.activeJobIdByMessageId.get(job.messageId) === jobId) {
      this.activeJobIdByMessageId.delete(job.messageId)
    }
    this.emit({
      type: "fact_check_error",
      jobId: job.jobId,
      sessionId: job.sessionId,
      sessionType: job.sessionType,
      messageId: job.messageId,
      error: new Error("Fact check was cancelled"),
    })
    this.scheduleJobCleanup(job, 0)
  }

  private buildCompletedResultKey(options: CompletedFactCheckResultLookup): string {
    return `${options.sessionType}:${options.sessionId ?? ""}:${options.messageId}`
  }

  private cleanupCompletedResults(now: number = Date.now()): void {
    for (const [key, entry] of this.completedResultsByKey) {
      if (now - entry.completedAt > COMPLETED_RESULT_TTL_MS) {
        this.completedResultsByKey.delete(key)
      }
    }
  }

  private storeCompletedResult(options: Omit<CompletedFactCheckResult, "completedAt">): void {
    const completedAt = Date.now()
    this.cleanupCompletedResults(completedAt)
    this.completedResultsByKey.set(this.buildCompletedResultKey(options), {
      ...options,
      completedAt,
    })
  }

  /**
   * Start a background fact-check job and return its id + a promise for the result.
   * If a job for the same message is already running, the existing job is reused
   * so callers share progress and completion.
   */
  start(options: StartFactCheckJobOptions): { jobId: string; promise: Promise<FactCheckResult> } {
    const { messageId } = options
    if (messageId) {
      const existingJobId = this.activeJobIdByMessageId.get(messageId)
      const existing = existingJobId ? this.jobsById.get(existingJobId) : undefined
      if (existing && existing.status === "running") {
        return {
          jobId: existing.jobId,
          promise: this.waitForCompletion(existing.jobId),
        }
      }
    }

    const jobId = globalThis.crypto.randomUUID()
    const abortController = new AbortController()
    const job: FactCheckJob = {
      jobId,
      status: "running",
      sessionType: options.sessionType,
      sessionId: options.sessionId,
      messageId: options.messageId,
      request: options.request,
      startedAt: Date.now(),
      abortController,
    }

    this.jobsById.set(jobId, job)
    if (job.messageId) {
      this.activeJobIdByMessageId.set(job.messageId, jobId)
    }

    const promise = this.runJob(job, options.onActivity)
    return { jobId, promise }
  }

  private emit(event: FactCheckJobEvent) {
    const listenersSnapshot = Array.from(this.listeners)
    for (const listener of listenersSnapshot) {
      try {
        listener(event)
      } catch (e) {
        logger.warn("FactCheckJobManager listener failed", e as Error)
      }
    }

    publishCrossTabEvent({
      type: event.type === "fact_check_completed" ? "completed" : "error",
      sessionType: event.sessionType,
      sessionId: event.sessionId,
      messageId: event.messageId,
      completedAt: Date.now(),
      errorMessage: event.type === "fact_check_error" ? event.error.message : undefined,
    })
  }

  private async runJob(
    job: FactCheckJob,
    onActivity?: (message: string) => void
  ): Promise<FactCheckResult> {
    try {
      const settingsService = SettingsService.getInstance()
      const wsUrl = `${BACKEND_URI}/ws/fact-check`

      const handleMessage = (data: ServerMessage) => {
        if (data.type === "activity") {
          const activityMsg = data as ActivityMessage
          job.lastActivity = activityMsg
          onActivity?.(activityMsg.message)
        }
      }

      const client = new WebSocketClient({
        url: wsUrl,
        idToken: settingsService.token,
        requestData: job.request as unknown as ChatAppRequest,
        files: job.request.files,
        signal: job.abortController.signal,
        onMessage: handleMessage,
      })

      const result = (await client.getPromise()) as unknown as FactCheckResult
      if (job.cancelled) {
        throw new Error("Fact check was cancelled")
      }
      job.status = "completed"
      job.completedAt = Date.now()
      job.result = result

      if (job.messageId) {
        this.storeCompletedResult({
          sessionType: job.sessionType,
          sessionId: job.sessionId,
          messageId: job.messageId,
          result,
        })
      }

      this.emit({
        type: "fact_check_completed",
        jobId: job.jobId,
        sessionId: job.sessionId,
        sessionType: job.sessionType,
        messageId: job.messageId,
        result,
      })

      this.scheduleJobCleanup(job)

      return result
    } catch (e) {
      const error = e instanceof Error ? e : new Error("Fact check failed")
      if (job.abortController.signal.aborted) {
        const wasCancelled = job.cancelled
        job.status = "cancelled"
        job.cancelled = true
        job.error = error
        if (!wasCancelled) {
          this.emit({
            type: "fact_check_error",
            jobId: job.jobId,
            sessionId: job.sessionId,
            sessionType: job.sessionType,
            messageId: job.messageId,
            error,
          })
        }
        this.scheduleJobCleanup(job)
        throw error
      }
      job.status = "error"
      job.completedAt = Date.now()
      job.error = error
      if (job.messageId && this.activeJobIdByMessageId.get(job.messageId) === job.jobId) {
        this.activeJobIdByMessageId.delete(job.messageId)
      }
      this.emit({
        type: "fact_check_error",
        jobId: job.jobId,
        sessionId: job.sessionId,
        sessionType: job.sessionType,
        messageId: job.messageId,
        error,
      })
      this.scheduleJobCleanup(job)
      throw error
    }
  }

  private waitForCompletion(jobId: string): Promise<FactCheckResult> {
    const existing = this.jobsById.get(jobId)
    if (!existing) {
      return Promise.reject(new Error("Fact check job not found"))
    }
    if (existing.status === "completed") {
      if (existing.result) {
        return Promise.resolve(existing.result)
      }
      return Promise.reject(
        new Error("Fact check already completed. Use getCompletedResult to retrieve the result.")
      )
    }
    if (existing.status === "error") {
      return Promise.reject(existing.error ?? new Error("Fact check already failed"))
    }
    if (existing.status === "cancelled") {
      return Promise.reject(new Error("Fact check was cancelled"))
    }

    return new Promise((resolve, reject) => {
      const unsubscribe = this.subscribe((event) => {
        if (event.jobId !== jobId) return
        unsubscribe()
        if (event.type === "fact_check_completed") {
          resolve(event.result)
        } else {
          reject(event.error)
        }
      })

      // Re-check job status after subscribing to avoid race conditions.
      // If the job completes between the initial status check (lines 318-335) and setting up
      // the subscription, we could miss the completion event and the promise would hang.
      // This re-check ensures we catch any status changes that occurred during subscription setup.
      const current = this.jobsById.get(jobId)
      if (!current) {
        unsubscribe()
        reject(new Error("Fact check job not found"))
        return
      }
      if (current.status === "completed") {
        unsubscribe()
        if (current.result) {
          resolve(current.result)
        } else {
          reject(
            new Error(
              "Fact check already completed. Use getCompletedResult to retrieve the result."
            )
          )
        }
        return
      }
      if (current.status === "error") {
        unsubscribe()
        reject(current.error ?? new Error("Fact check already failed"))
        return
      }
      if (current.status === "cancelled") {
        unsubscribe()
        reject(new Error("Fact check was cancelled"))
        return
      }
    })
  }

  private scheduleJobCleanup(job: FactCheckJob, delayMs: number = 1000): void {
    const cleanup = () => {
      if (job.messageId && this.activeJobIdByMessageId.get(job.messageId) === job.jobId) {
        this.activeJobIdByMessageId.delete(job.messageId)
      }
      this.jobsById.delete(job.jobId)
      this.cleanupTimers.delete(job.jobId)
    }

    const existingTimer = this.cleanupTimers.get(job.jobId)
    if (existingTimer !== undefined) {
      clearTimeout(existingTimer)
      this.cleanupTimers.delete(job.jobId)
    }

    if (delayMs <= 0) {
      cleanup()
      return
    }

    const timerId = setTimeout(() => {
      cleanup()
    }, delayMs)

    this.cleanupTimers.set(job.jobId, timerId)
  }
}
